import { Flex } from '@osmosis/tailwind-ui/components/Box';
import React, { useRef, MouseEvent } from 'react';
import { useSearchContext } from '../../context/SearchProvider';
import TopicsListDropdown from '../header/TopicsListDropdown';
import { FormattedMessage } from 'react-intl';
import useModal from '../../hooks/useModal';
import useClickOutside from '../../hooks/useClickOutside';
import useSearchAiToggle from '../../hooks/useSearchAiToggle';
import { faLightbulbOn } from '@fortawesome/pro-regular-svg-icons/faLightbulbOn';
import { faCircle } from '@fortawesome/free-solid-svg-icons/faCircle';
import DashboardSearchTip from './DashboardSearchTip';
import SearchContainer from './SearchContainer';
import useOsmosisAI from '../../hooks/useOsmosisAI';
import {
  useContentSize,
  useEmbedContext,
  usePagesContext,
  useUserContext,
} from '@osmosis/nextjs-components/context';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { track } from '../../lib/util/tracking';
import { getSearchContainerClasses } from './searchStyles';
import { DashboardContextId } from '../../app/_lib/backend/aiContext/ids';
import { buildOsmosisAIUrl } from '../../lib/util/oai';

type Props = {
  dataTestId?: string;
  showTips?: boolean;
  showTopicsDropdown?: boolean;
  /** Renders the standalone "Ask Osmosis AI" CTA in the old design. */
  showAIButton?: boolean;
  source?: string;
  centerSearch?: boolean;
  /** Renders the Search / AI Chat toggle instead of the standalone AI CTA. */
  isNewDashboard?: boolean;
};

/**
 * DashboardSearch.
 */
const DashboardSearch = ({
  dataTestId = 'dashboard-search',
  showTips = true,
  showTopicsDropdown = true,
  showAIButton = true,
  source = 'Dashboard',
  isNewDashboard = false,
  centerSearch = false,
}: Props = {}) => {
  const { isSearchResultsOpen, search, setSearchValue } = useSearchContext();
  const { push } = usePagesContext();
  const { showAIButton: isOsmosisAiEligible } = useOsmosisAI();
  const { getPageLocation } = useEmbedContext();
  const { pathname } = getPageLocation();
  const topicsDropdown = useModal();

  const {
    mode,
    focusInputOnSwitch,
    handleModeChange,
    showNewSearchDesign,
    isAiChatMode,
  } = useSearchAiToggle(isNewDashboard, isOsmosisAiEligible);

  const handleAiSubmit = (value: string) => {
    if (!value.trim()) return;
    track('osmosis-ai-toggle-prompt-submitted', { page: pathname, source }, [
      'keen',
      'pendo',
    ]);
    push(
      buildOsmosisAIUrl({
        query: value,
        dashboard: DashboardContextId.Default,
      }),
    );
  };

  const { getClassValue } = useContentSize();

  const { user } = useUserContext();

  const topicsListDropdownRef = useRef<HTMLDivElement | null>(null);

  const topicsDropdownButtonRef = useRef<HTMLButtonElement | null>(null);

  useClickOutside([topicsListDropdownRef, topicsDropdownButtonRef], () => {
    if (topicsDropdown.isVisible) {
      topicsDropdown.hide();
    }
  });

  const dropdownText = (
    <FormattedMessage
      id="dashboard.welcomeHeader.alltopics"
      defaultMessage="View all topics"
    />
  );

  const toggleDropdown = (
    event: MouseEvent<HTMLDivElement> | MouseEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();
    topicsDropdown.toggle();
  };

  const searchStyles = cn(
    getSearchContainerClasses({ isSearchResultsOpen }),
    showNewSearchDesign ? '!shadow-none' : '',
  );

  const tipsClassName = getClassValue({
    defaultClasses: 'hidden sm:flex',
    breakpoints: { sm: 'mb-1 flex', base: 'hidden' },
  });

  return (
    <Flex
      className={cn(
        `relative m-auto flex-col items-center justify-center `,
        isSearchResultsOpen ? 'justify-start' : 'justify-center',
        showNewSearchDesign ? 'mb-12' : '',
      )}
    >
      <Flex
        data-testid="search-container"
        className={` relative w-full  flex-grow flex-col items-center justify-start ${
          isSearchResultsOpen
            ? 'max-w-dashboard-search-open'
            : 'max-w-dashboard-search'
        }`}
      >
        <SearchContainer
          showNewSearchDesign={showNewSearchDesign}
          showAIButton={showAIButton}
          mode={mode}
          onModeChange={handleModeChange}
          toggleSource={isSearchResultsOpen ? 'Search' : source}
          centerSearch={centerSearch}
          isAiChatMode={isAiChatMode}
          containerClasses={searchStyles}
          dataTestId={dataTestId}
          focusOnMount={focusInputOnSwitch}
          aiChatValue={search}
          onAiChatValueChange={setSearchValue}
          onAiSubmit={handleAiSubmit}
          showTopicsDropdownButton={
            !!(showTopicsDropdown && user && !isAiChatMode)
          }
          topicsDropdownButtonRef={topicsDropdownButtonRef}
          dropdownText={dropdownText}
          isTopicsDropdownVisible={topicsDropdown.isVisible}
          onToggleTopicsDropdown={toggleDropdown}
        />

        {showTips && !isSearchResultsOpen && !showNewSearchDesign && (
          <Flex
            data-testid="dashboard-search-tips"
            className={cn('mb-[60px] mt-4 self-start ', tipsClassName)}
          >
            <DashboardSearchTip
              icon={faLightbulbOn}
              iconClassName="text-orange-400 mr-2 w-[30px] h-[30px]"
              dataTestId="dashboard-search-lightbulb-icon"
              type="renal"
            />

            <DashboardSearchTip
              icon={faCircle}
              iconClassName="text-slate-600 mx-2 w-[8px] h-[8px]"
              type="aminoAcids"
            />

            <DashboardSearchTip
              icon={faCircle}
              iconClassName="text-slate-600 mx-2 w-[8px] h-[8px]"
              type="pharmacokinetics"
            />
          </Flex>
        )}
      </Flex>

      <TopicsListDropdown
        ref={topicsListDropdownRef}
        isVisible={topicsDropdown.isVisible}
        hide={topicsDropdown.hide}
        dataTestid={'dropdown-container'}
        isSearchResultsOpen={isSearchResultsOpen}
        motionContainerStyle={{ width: '100%' }}
      />
    </Flex>
  );
};

export default DashboardSearch;
